/* eslint-disable lit-a11y/accessible-name */
import {
  LitElement,
  classMap,
  html,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import { createPopper } from '@popperjs/core';
import style from './style.scss';

class Tooltip extends LitElement {
  /**
   * ID of the tooltip element, needed for a11y labeling
   *
   * @type {string}
   */
  @property({ type: String, attribute: 'tooltip-id' })
  accessor tooltipId = '';

  /**
   * Default placement of the open tooltip relative to its trigger element
   *
   * @type {('top'|'bottom'|'left'|'right')}
   */
  @property({ type: String })
  accessor placement = 'bottom';

  /**
   * @type {boolean}
   */
  @state()
  accessor _isActive = false;

  /**
   * @type {?object}
   */
  @state()
  accessor _instance = null;

  static get styles() {
    return [style];
  }

  /**
   * @returns {object} - popper.js default options
   */
  get popperConfig() {
    return {
      placement: this.placement,
      strategy: 'absolute',
      modifiers: [
        {
          name: 'arrow',
          options: {
            element: '.tooltip__arrow',
            padding: 10,
          },
        },
        {
          name: 'eventListeners',
          options: {
            scroll: false,
            resize: false,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['right', 'top', 'auto'],
          },
        },
        {
          name: 'offset',
          options: {
            offset: ({ placement }) => {
              if (placement.includes('start')) {
                return [-10, 0];
              }
              if (placement.includes('end')) {
                return [10, 0];
              }

              return [0, 0];
            },
          },
        },
      ],
    };
  }

  /**
   * @returns {Element}
   */
  get tooltipElement() {
    return this.shadowRoot.querySelector(`.tooltip`);
  }

  /**
   * @returns {Element}
   */
  get triggerElement() {
    return this.shadowRoot.querySelector(`[data-tooltip-trigger]`);
  }

  /**
   * Shows tooltip and recalculates its position
   */
  async showTooltip() {
    this._isActive = true;
    await this._instance.update();
    this.requestUpdate();
  }

  /**
   * Hides tooltip
   */
  hideTooltip() {
    this._isActive = false;
  }

  /**
   * Shows tooltip in case mouse entered the tooltip trigger element
   */
  @bound
  handleMouseEnter() {
    this.showTooltip();
  }

  /**
   * Hides tooltip in case mouse left the tooltip inner
   *
   * @param {Event} event
   */
  @bound
  handleMouseLeave(event) {
    const shouldHideTooltip = !this.tooltipElement.contains(event.relatedTarget);

    if (shouldHideTooltip) {
      this.hideTooltip();
    }
  }

  /**
   * @param {Event} event
   */
  @bound
  handleKeydown(event) {
    if (this._isActive && event.code === 'Escape') {
      this.hideTooltip();
    }
  }

  @bound
  handleClick() {
    this.showTooltip();
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   *
   * Calls tooltip initialization
   *
   * @see initTooltip
   */
  firstUpdated() {
    super.firstUpdated();

    this._instance = createPopper(this.triggerElement, this.tooltipElement, this.popperConfig);

    document.addEventListener('keydown', this.handleKeydown);
    this.triggerElement.addEventListener('mouseenter', this.handleMouseEnter);
    this.triggerElement.addEventListener('mouseleave', this.handleMouseLeave);
    this.triggerElement.addEventListener('click', this.handleClick);
    this.tooltipElement.addEventListener('mouseleave', this.handleMouseLeave);
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is removed from a document-connected element
   *
   * Remove event listeners and destroy instance of `Popper`
   */
  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.triggerElement.removeEventListener('mouseenter', this.handleMouseEnter);
    this.triggerElement.removeEventListener('mouseleave', this.handleMouseLeave);
    this.triggerElement.removeEventListener('click', this.handleClick);
    this.tooltipElement.removeEventListener('mouseleave', this.handleMouseLeave);

    this._instance.destroy();

    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  render() {
    return html`
      <button
        type="button"
        class="info-tooltip ${classMap({ 'is-active': this._isActive })}"
        data-tooltip-trigger="true"
        aria-label="Doplnkové informácie"
        aria-owns=${this.tooltipId}
      >
        <slot name="tooltip-trigger"></slot>
      </button>
      <div
        id=${this.tooltipId}
        class="tooltip ${classMap({ 'is-visible': this._isActive })}"
        role="tooltip"
        ?hidden=${!this._isActive}
        data-tooltip="true"
        data-popper-placement=${this.placement}
      >
        <span class="tooltip__arrow"></span>
        <span class="tooltip__dialog">
          <slot name="tooltip-inner"></slot>
        </span>
      </div>
    `;
  }
}

// In case of duplicate custom element registration due to multiple
// instances of the element used, we want to skip the second registration causing error.
if (!customElements.get('osk-tooltip')) {
  customElements.define('osk-tooltip', Tooltip);
}
